import React, { useState } from "react";
import AccountActionButton from "./AccountActionButton";
import DeleteAccountModal from "./DeleteAccountModal";
import userController from "../../controllers/userController";
import { userService } from "../../services/userService";
import Utils from "../../utils/Utils";
import { translateLabel, translateText, useTranslationSnapshot } from "./../../translations/runtime";


function DeleteAccountPanel({ onCancel, onDeleted }) {
  useTranslationSnapshot();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const [isDeleting, setIsDeleting] = useState(false);

  const [deleteError, setDeleteError] = useState("");

  // =========================================================
  // GET JWT FROM STORED USER
  // =========================================================
  const getJwtFromStoredUser = (storedUser) => {
    if (typeof storedUser === "string") {
      return storedUser;
    }

    return (
      storedUser?.jwt ||
      storedUser?.token ||
      storedUser?.accessToken ||
      storedUser?.access_token ||
      ""
    );
  };

  // =========================================================
  // GET USER ID FROM JWT PAYLOAD
  // =========================================================
  const getUserIdFromPayload = (payload = {}) =>
    String(
      payload?.id ||
        payload?.userId ||
        payload?.userID ||
        payload?.UserId ||
        payload?.UserID ||
        payload?.sub ||
        payload?.user?.id ||
        payload?.user?.userId ||
        payload?.user?.userID ||
        "",
    ).trim();

  // =========================================================
  // RESOLVE AUTHENTICATED SESSION
  // =========================================================
  const resolveAuthenticatedSession = () => {
    const storedUser = userService.getJwt();

    const jwt = String(getJwtFromStoredUser(storedUser) || "").trim();

    const payload =
      (jwt ? Utils.parseJwt(jwt) : null) ||
      userService.getUserPayload?.() ||
      {};

    const userID = getUserIdFromPayload(payload);

    return {
      jwt,
      userID,
    };
  };

  // =========================================================
  // OPEN DELETE MODAL
  //
  // NO API HERE.
  // This button only opens the popup.
  // =========================================================
  const handleOpenDeleteModal = () => {
    setDeleteError("");
    setIsDeleteModalOpen(true);
  };

  // =========================================================
  // CLOSE DELETE MODAL
  // =========================================================
  const handleCloseDeleteModal = () => {
    if (isDeleting) {
      return;
    }

    setDeleteError("");
    setIsDeleteModalOpen(false);
  };

  // =========================================================
  // CONFIRM DELETE ACCOUNT
  //
  // CALLED ONLY BY DELETE BUTTON INSIDE POPUP.
  //
  // DELETE /api/user/v1/deleteUserByUserId
  // Query: userID
  // =========================================================
  const handleConfirmDelete = async () => {
    if (isDeleting) {
      return;
    }

    setDeleteError("");

    try {
      const { jwt, userID } = resolveAuthenticatedSession();

      if (!jwt) {
        throw new Error("Authentication token was not found.");
      }

      if (!userID) {
        throw new Error("User ID was not found.");
      }

      setIsDeleting(true);

      const response = await userController.deleteUserAccount({
        userID,
        jwt,
      });

      const responseData =
        response?.data?.data ??
        response?.data?.result ??
        response?.data ??
        response;

      const status = response?.status ?? response?.statusCode;

      const deleteSucceeded =
        responseData === true ||
        responseData === "true" ||
        status === 200 ||
        status === 204;

      if (!deleteSucceeded) {
        throw new Error("Delete account request was not successful.");
      }

      setIsDeleteModalOpen(false);

      // Logout / redirect should be handled by parent.
      onDeleted?.({
        response,
        userID,
      });
    } catch (error) {
      console.error("[DeleteAccountPanel] Delete account failed:", error);

      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Unable to delete your account. Please try again.";

      setDeleteError(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="vendor-delete-account-content">
      <h2 className="vendor-delete-account-title">{translateLabel("txt_end_user_delete_account", "Delete Account")}</h2>

      <div className="vendor-delete-account-description">
        <p>{translateLabel("txt_web_vendor_account_deletion_request_description", "You are about to request deletion of your D2i2a w Noss account.")}</p>

        <p>
          <span className="vendor-delete-account-warning">{translateLabel("txt_web_vendor_review_account_deletion", "Review your decision before confirming account deletion.")}</span>
        </p>

        <p className="vendor-delete-account-recovery-text">
          {translateLabel("txt_web_vendor_cancel_before_deletion_confirmation", "You can cancel before confirming this request.")}
        </p>
      </div>

      {deleteError ? (
        <div className="vendor-delete-account-error" role="alert">
          {translateText(deleteError)}
        </div>
      ) : null}

      <div className="vendor-delete-account-actions">
        <AccountActionButton
          color="danger"
          onClick={handleOpenDeleteModal}
          type="button"
          width="85px"
        > {translateLabel("lnk_delete", "Delete")} </AccountActionButton>

        <AccountActionButton
          color="dark"
          onClick={onCancel}
          type="button"
          width="85px"
        > {translateLabel("btn_cancel", "Cancel")} </AccountActionButton>
      </div>

      <DeleteAccountModal
        loading={isDeleting}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        open={isDeleteModalOpen}
      />
    </div>
  );
}

export default DeleteAccountPanel;
